import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import config from 'config';
import { loadStripe, type Stripe as StripeJs } from '@stripe/stripe-js';
import { CheckoutElementsProvider } from '@stripe/react-stripe-js/checkout';
import type { CheckoutInterval } from '@kimuno/api-sdk/lib/types/requests/Billing';
import billingService from '../../../services/billing.service';
import useRouter from '../../../utils/useRouter';
import useScreenSize from '../../../utils/useScreenSize';
import useCaptcha from '../../../utils/useCaptcha';
import { setCustomPageTitle } from '../../../utils/customTitle';
import { useAmplitudeExperiment } from '../../../utils/ampli';
import { currencyFormat } from '../../../utils/format';
import { palette, spaces } from '../../../assets/styles/themes/tokens';
import AppNavBar from '../../../components/AppLayout/MasterLayout/AppNavBar';
import StripeCheckoutSummary from '../StripeCheckoutSummary/StripeCheckoutSummary';
import {
  ERROR_LABELS,
  PLAN_LABELS,
  REF_RABATT_CODE,
  REF_RABATT_PLANS,
  isRefRabattCode,
} from './checkout.constants';
import {
  checkoutSignature,
  isValidInterval,
  isValidPlan,
  readQueryParam,
  resolveCheckoutAttribution,
} from './checkout.helpers';
import type {
  CheckoutErrorKind,
  IntervalSyncState,
  PromoSyncState,
  StripeCheckout,
} from './checkout.types';
import { useCheckoutSessions } from './hooks/useCheckoutSessions';
import { useEmailCheck } from './hooks/useEmailCheck';
import ColumnContainer from './components/ColumnContainer';
import EmailInputSection from './components/EmailInputSection';
import OrderTotalSummary from './components/OrderTotalSummary';
import CheckoutPriceTable from './components/CheckoutPriceTable';
import CheckoutPaymentElement from './components/CheckoutPaymentElement';
import CheckoutTerms from './components/CheckoutTerms';
import TrialCostNotice from './components/TrialCostNotice';
import { PaymentFormSkeleton } from './components/CheckoutSkeletons';

const StripeCheckoutPage: React.FC = () => {
  const { t } = useTranslation();
  const { match: { location: { query } } } = useRouter();
  const { isLgScreen, isMdScreen } = useScreenSize();
  const isDesktopScreen = isLgScreen || isMdScreen;
  const queryRecord = query as Record<string, string | undefined>;

  const planParam = readQueryParam(queryRecord, 'plan');
  const intervalParam = readQueryParam(queryRecord, 'interval');
  const initialPromoFromUrl = readQueryParam(queryRecord, 'promocode') ?? '';
  // Truthy variants of `?trial=...` flip the session into trial mode. Length
  // is fixed kimuno-side — this URL flag is the only knob the caller has.
  const trialParam = readQueryParam(queryRecord, 'trial');
  const trial = trialParam === 'true' || trialParam === '1';

  const plan = isValidPlan(planParam) ? planParam : null;
  const initialInterval = isValidInterval(intervalParam) ? intervalParam : null;
  // A ref-rabatt code in the URL drives the Ref-Rabatt toggle instead of the
  // manual promo input.
  const promoIsRefRabatt = initialPromoFromUrl.toLowerCase() === REF_RABATT_CODE.toLowerCase();
  const initialPromoInput = promoIsRefRabatt ? '' : initialPromoFromUrl;

  const stripePromise = useMemo<Promise<StripeJs | null>>(
    () => loadStripe(config.stripe.publishableKey),
    [],
  );
  const [interval, setIntervalValue] = useState<CheckoutInterval | null>(initialInterval);

  // SM-405: when the URL carries no valid interval, the `payment-interval`
  // Amplitude experiment picks the default for anonymous visitors (bucketed by
  // device_id — the page is RedirectIfLoggedIn, so only anonymous users reach
  // it). An explicit `?interval=` is respected and the experiment isn't run.
  const { isReady: intervalExperimentReady, variant: intervalVariant } = useAmplitudeExperiment(
    undefined,
    initialInterval === null ? config.amplitudeExperiments.paymentInterval : undefined,
    { allowAnonymous: true },
  );
  const [promoInput, setPromoInput] = useState(initialPromoInput);
  const [appliedPromo, setAppliedPromo] = useState(initialPromoInput);
  // Ref-Rabatt toggle — surfaced in trial mode and when the URL carries the code.
  const [refRabattOn, setRefRabattOn] = useState(promoIsRefRabatt);

  const [email, setEmail] = useState('');
  const emailCheckState = useEmailCheck(email);

  // SM-345: the email the checkout sessions should be bound to. Recreate-on-settle:
  // a churned (`reusable-customer`) email binds the session to the existing Stripe
  // customer; a plain-new (`available`) email leaves it unbound (auto-create) —
  // which is a no-op when already unbound, so the common path never recreates.
  // Non-settled / blocked states leave the current binding untouched (Pay is
  // guarded off in those states), so completing never lands on a stale customer.
  const [boundEmail, setBoundEmail] = useState<string | null>(null);
  useEffect(() => {
    // Driven off the settled state only (not the raw `email`, which leads the
    // check by a render): `reusable-customer` carries its own checked email, so
    // the binding can never lag onto a stale value. `available` unbinds (a no-op
    // when already unbound → the common path never recreates). Non-settled /
    // blocked states leave the current binding untouched (Pay is guarded off).
    if (emailCheckState.kind === 'reusable-customer') {
      setBoundEmail(emailCheckState.email);
    } else if (emailCheckState.kind === 'available') {
      setBoundEmail(null);
    }
  }, [emailCheckState]);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [newsletter, setNewsletter] = useState(true);
  const [renderCaptcha, isCaptchaSolved] = useCaptcha();

  // Promo state driven by the in-place apply/remove on the live session (via
  // <CheckoutPaymentElement>): `applying` disables the summary + submit while a
  // code is being applied/removed; `promoError` renders the inline invalid-code
  // message under the promo input.
  const [applying, setApplying] = useState(false);
  const [promoError, setPromoError] = useState(false);

  // SM-375: interval-swap lifecycle on the live session. `intervalUpdating` dims
  // + disables the summary while a swap is in flight. The `interval` selector is
  // optimistic (reflects the user's pick immediately); `appliedIntervalRef`
  // holds the last successfully-applied interval so a failed swap can revert it.
  const [intervalUpdating, setIntervalUpdating] = useState(false);
  const appliedIntervalRef = useRef<CheckoutInterval | null>(initialInterval);
  const intervalRef = useRef<CheckoutInterval | null>(initialInterval);
  intervalRef.current = interval;
  // SM-405: the last valid URL-derived interval. When the URL override goes from
  // a valid value to absent/invalid, we clear the selected interval so the
  // payment-interval experiment can re-pick the default — but only on that
  // transition, so a user's dropdown pick (which never changes the URL) survives.
  const prevUrlIntervalRef = useRef<CheckoutInterval | null>(initialInterval);

  // The live session, lifted out of its provider by <CheckoutPaymentElement>
  // so prices + submit render at page level (stable across interval swaps and
  // promo changes — the element never remounts, so the captcha never does).
  const [activeCheckout, setActiveCheckout] = useState<StripeCheckout | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // SM-345: drop the live checkout the moment the binding target changes. The
  // session pair is about to be recreated (new clientSecret → <PaymentElement>
  // remounts), so the current `activeCheckout` belongs to the *previous*
  // binding. Clearing it forces `submitDisabled` (via `!activeCheckout?.canConfirm`)
  // until the rebound session reports — closing the window after `boundTo`/
  // `pending` have synced but before the remounted element reports, where Pay
  // could otherwise confirm on the stale (e.g. still-unbound) session and mint
  // the duplicate customer this ticket prevents.
  useEffect(() => {
    setActiveCheckout(null);
  }, [boundEmail]);
  // `useCheckoutElements()` hands back a fresh `checkout` object on every
  // render, so commit to state only when something we render from actually
  // changed — otherwise React would re-render in a loop as the bridge re-reports
  // each pass. The signature spans id, card-completeness *and* the price fields,
  // so an in-place promo change (same id, new prices) still commits.
  const handleCheckoutReport = useCallback((checkout: StripeCheckout | null) => {
    // Ignore the transient `null` while a session reloads (e.g. applying a
    // promo / Ref-Rabatt) — keep the last-known checkout so the price notices
    // stay on screen and only their values refresh once the new session lands.
    if (!checkout) return;
    setActiveCheckout((prev) => {
      if (prev === checkout) return prev;
      if (prev && checkoutSignature(prev) === checkoutSignature(checkout)) return prev;
      return checkout;
    });
  }, []);

  const showRefRabattToggle =
    plan !== null && REF_RABATT_PLANS.includes(plan) && (trial || promoIsRefRabatt);
  // The Ref-Rabatt toggle wins over a manually-applied code — except a
  // hand-typed Ref-Rabatt variant (e.g. `ref-rabatt-40`), whose exact code is
  // preserved so its discount isn't silently swapped for the toggle's default.
  const effectivePromo = (
    refRabattOn
      ? isRefRabattCode(appliedPromo)
        ? appliedPromo
        : REF_RABATT_CODE
      : appliedPromo
  ).trim();

  useEffect(() => {
    // SM-405: the URL drives the interval when it carries a valid one. When it
    // doesn't, leave the default to the payment-interval experiment effect below.
    // Two cases for an absent/invalid URL interval:
    //  - the URL never had one (mount / prevUrlIntervalRef still null) → do NOT
    //    touch the interval, so we don't clobber the experiment default in the
    //    same commit (that race is the original bug this guard fixed);
    //  - the URL *had* a valid one that's now gone (a genuine navigation) → clear
    //    the selected interval so the experiment re-picks. This only fires on the
    //    URL transition, so a user's dropdown pick (URL unchanged) is preserved.
    const nextInterval = isValidInterval(intervalParam) ? intervalParam : null;
    if (nextInterval !== null) {
      setIntervalValue(nextInterval);
      appliedIntervalRef.current = nextInterval;
    } else if (prevUrlIntervalRef.current !== null) {
      setIntervalValue(null);
      appliedIntervalRef.current = null;
    }
    prevUrlIntervalRef.current = nextInterval;
    setPromoInput(initialPromoInput);
    setAppliedPromo(initialPromoInput);
    setRefRabattOn(promoIsRefRabatt);
  }, [intervalParam, initialPromoInput, promoIsRefRabatt]);

  // SM-405: when the URL gave no valid interval, the payment-interval experiment
  // picks the default for anonymous visitors. Runs once the experiment resolves;
  // falls back to 'monthly' if it yields no valid variant. Seeds
  // `appliedIntervalRef` too so a later interval swap has a valid revert target.
  useEffect(() => {
    if (initialInterval !== null || interval !== null || !intervalExperimentReady) return;
    const resolved: CheckoutInterval = isValidInterval(intervalVariant) ? intervalVariant : 'monthly';
    setIntervalValue(resolved);
    appliedIntervalRef.current = resolved;
  }, [initialInterval, interval, intervalExperimentReady, intervalVariant]);

  // Browser tab title (distinct from the in-page nav-bar title).
  useEffect(() => {
    setCustomPageTitle(t('Create account'));
  }, [t]);

  const handlePromoFallback = useCallback(() => {
    // Clear the toggle BEFORE the applied code. React 17 doesn't batch the
    // setState calls fired from Stripe's async promo callback, so clearing
    // `appliedPromo` while `refRabattOn` is still on produces an intermediate
    // render where `effectivePromo` resolves to the bare REF_RABATT_CODE — a
    // real 50% coupon — which gets applied for a beat and lingers as a phantom
    // discount after an invalid `ref-rabatt-*` code. Order matters.
    setRefRabattOn(false);
    setPromoInput('');
    setAppliedPromo('');
  }, []);

  const { secret, country, errorCode, setErrorCode, boundTo, pending } = useCheckoutSessions({
    plan,
    interval,
    trial,
    newsletterConsent: newsletter,
    enabled: plan !== null && interval !== null,
    boundEmail,
  });

  // Bridges the live-session promo apply/remove lifecycle to the summary UI. An
  // invalid code clears the promo input (fallback); a network failure surfaces
  // the page-level error banner.
  const handlePromoSync = useCallback(
    (state: PromoSyncState) => {
      setApplying(state.applying);
      if (state.error === 'invalid') {
        setPromoError(true);
        handlePromoFallback();
      } else if (state.error === 'network') {
        setErrorCode('NetworkError');
      } else if (!state.applying) {
        setPromoError(false);
      }
    },
    [handlePromoFallback, setErrorCode],
  );

  // SM-375: bridges the live-session interval-swap lifecycle to the summary UI.
  // On success the optimistic selector value becomes the applied interval; on
  // failure it reverts to the last-applied interval and the page-level error
  // banner surfaces — so the buyer never sees a price/interval mismatch.
  const handleIntervalSync = useCallback(
    (state: IntervalSyncState) => {
      setIntervalUpdating(state.updating);
      if (state.error === 'network') {
        setErrorCode('NetworkError');
        setIntervalValue(appliedIntervalRef.current);
      } else if (!state.updating) {
        appliedIntervalRef.current = intervalRef.current;
      }
    },
    [setErrorCode],
  );

  const handleIntervalChange = useCallback((next: CheckoutInterval) => {
    setErrorCode(null);
    setIntervalValue(next);
  }, [setErrorCode]);

  const handleApplyPromo = useCallback(() => {
    setPromoError(false);
    const trimmed = promoInput.trim();
    setAppliedPromo(trimmed);
    // Applying a Ref-Rabatt code by hand should light up the toggle so it
    // reflects the active trainee discount. The code itself is kept as-is
    // (see `effectivePromo`), so a `-40` variant still bills at its own rate.
    if (showRefRabattToggle && isRefRabattCode(trimmed)) {
      setRefRabattOn(true);
    }
  }, [promoInput, showRefRabattToggle]);

  const appliedPromoLabel = effectivePromo;

  const handleRemoveAppliedPromo = useCallback(() => {
    setPromoError(false);
    // Toggle off before the code — see handlePromoFallback: clearing a
    // ref-rabatt code while the toggle is still on would transiently re-apply
    // the bare REF_RABATT_CODE and leave a phantom discount.
    setRefRabattOn(false);
    setPromoInput('');
    setAppliedPromo('');
  }, [setPromoError]);

  const handleRefRabattChange = useCallback((next: boolean) => {
    setPromoError(false);
    setRefRabattOn(next);
    if (!next) {
      setPromoInput('');
      setAppliedPromo('');
    }
  }, [setPromoError]);

  const handleSubmitError = useCallback((kind: CheckoutErrorKind) => {
    setErrorCode(kind);
  }, [setErrorCode]);

  const handleTermsChange = useCallback((value: boolean) => {
    setTermsAccepted(value);
    if (value) setTermsError(false);
  }, []);

  // SM-345: `reusable-customer` is payable like `available`. The Pay-guard
  // additionally requires the live sessions to reflect the intended binding
  // (`bindingSynced`) and no recreate to be in flight (`pending`) — so a buyer
  // can never complete on a session still bound to a previous email.
  const canSubmitPayment =
    emailCheckState.kind === 'available' || emailCheckState.kind === 'reusable-customer';
  // SM-380: `boundEmail` trails the settled check by a render (it's set in an
  // effect), and `confirm()` reads `boundEmail` to decide whether to send the
  // `email` override. Gate Pay on the settled email already agreeing with
  // `boundEmail` so confirm() never fires in that one-render window — where an
  // `available` email could still see a truthy `boundEmail` (omit → charge the
  // wrong customer) or a `reusable-customer` email a `null` one (send override
  // → mint the SM-345 duplicate).
  const emailBindingSynced =
    emailCheckState.kind === 'reusable-customer'
      ? emailCheckState.email === boundEmail
      : emailCheckState.kind === 'available' && boundEmail === null;
  const bindingSynced = boundTo === boundEmail;
  const canPay =
    canSubmitPayment && emailBindingSynced && bindingSynced && !pending && termsAccepted && isCaptchaSolved;

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!termsAccepted) {
        setTermsError(true);
        return;
      }
      if (!activeCheckout || !canPay || submitting || applying || intervalUpdating) return;

      setSubmitting(true);
      // SM-284: the session carries the mount-time newsletter default and isn't
      // recreated on a toggle (SM-273), so push the final state before confirm.
      // Best-effort — a failed consent update must not block the payment.
      // SM-309: attribution is re-resolved at submit time so a long-lived page
      // refreshes last touch; omitted entirely when nothing resolves.
      const attribution = resolveCheckoutAttribution();
      try {
        await billingService.updateCheckoutSession(activeCheckout.id, {
          newsletterConsent: newsletter,
          ...(attribution ? { attribution } : {}),
        });
      } catch {
        // Swallowed — newsletter consent is secondary to completing payment.
      }
      try {
        // SM-345: a session bound to a reusable customer already carries that
        // customer's email server-side. Passing `email` to confirm() then throws
        // Stripe `IntegrationError` ("cannot set email … customer with an email
        // is already set"). Only send it on the unbound (auto-create) path.
        const result = await activeCheckout.confirm({
          ...(boundEmail ? {} : { email: email.trim() }),
          redirect: 'always',
        });
        // On success Stripe redirects (redirect: 'always'), so we intentionally
        // leave `submitting` true to hold the button disabled during navigation;
        // only a resolved error resets it below.
        if (result.type === 'error') {
          handleSubmitError(
            result.error.code === 'paymentFailed' ? 'CheckoutSessionFailed' : 'NetworkError',
          );
          setSubmitting(false);
        }
      } catch {
        // A thrown SDK/network error never redirects — surface it and re-enable.
        handleSubmitError('NetworkError');
        setSubmitting(false);
      }
    },
    [
      activeCheckout,
      canPay,
      submitting,
      applying,
      intervalUpdating,
      termsAccepted,
      email,
      boundEmail,
      newsletter,
      handleSubmitError,
    ],
  );

  if (!plan) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppNavBar title={t('Payment')} />
        <Box sx={{ padding: spaces.xl }}>
          <Alert severity='error' data-testid='stripe-checkout-invalid-link'>
            {t('Invalid checkout link — plan or interval is missing')}
          </Alert>
        </Box>
      </Box>
    );
  }

  // SM-405: `interval` is null only when the URL gave none — the payment-interval
  // experiment is resolving the default. Show a loading state (never the error
  // banner) until it settles, then the effect above sets the interval.
  if (!interval) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppNavBar title={t('Payment')} />
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: spaces.xl }}>
          <CircularProgress data-testid='stripe-checkout-interval-loading' />
        </Box>
      </Box>
    );
  }

  const renderSessionProvider = (secret: string, children: React.ReactNode) => (
    <CheckoutElementsProvider
      key={secret}
      stripe={stripePromise}
      options={{
        clientSecret: secret,
        // SM-252: seed the billing-address country with the IP-resolved country
        // (from the checkout-session response) instead of a hardcoded 'DE', so
        // the lazily-created Stripe customer — and the *first* invoice — match
        // the applied tax rate. The field itself stays hidden
        // (CheckoutPaymentElement `country: 'never'`); only the default changes.
        defaultValues: {
          billingAddress: { address: { country } },
        },
      }}
    >
      {children}
    </CheckoutElementsProvider>
  );

  const hasAnySession = secret !== null;

  // A single <PaymentElement>, kept mounted across interval and promo changes
  // (both applied in place on the live session, SM-375). Keyed by the session
  // secret so it only remounts on a genuine session recreation (plan/trial), not
  // on an interval swap — the card input survives.
  const paymentStack = (
    <Box data-testid='stripe-elements-checkout' sx={{ position: 'relative' }}>
      {secret !== null ? (
        renderSessionProvider(
          secret,
          <CheckoutPaymentElement
            email={email.trim()}
            canUpdateEmail={canSubmitPayment}
            promoCode={effectivePromo}
            interval={interval}
            onReport={handleCheckoutReport}
            onPromoSync={handlePromoSync}
            onIntervalSync={handleIntervalSync}
          />,
        )
      ) : (
        <Box data-testid='stripe-checkout-loading'>
          <PaymentFormSkeleton />
        </Box>
      )}
    </Box>
  );

  const mutating = applying || intervalUpdating;
  const submitDisabled = !canPay || submitting || mutating || !activeCheckout?.canConfirm;

  // Price reduction from the applied promo, over the 12-month term (yearly bills
  // the full term at once; monthly ×12). During a trial the discount lives on
  // `recurring.dueNext` (today's total is 0). Shown next to the applied-promo chip.
  const billingForDiscount = activeCheckout
    ? activeCheckout.recurring?.trial
      ? activeCheckout.recurring.dueNext
      : activeCheckout.total
    : null;
  const discountMinorUnits = billingForDiscount?.discount.minorUnitsAmount ?? 0;
  const promoReduction =
    discountMinorUnits > 0
      ? `-${currencyFormat((discountMinorUnits * (interval === 'yearly' ? 1 : 12)) / 100)}`
      : undefined;

  return (
    <Box
      data-testid='stripe-checkout-page-container'
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
      }}
    >
      <AppNavBar title={t('Payment')} />
      <Box
        data-testid='stripe-checkout-page'
        sx={{
          display: 'flex',
          flexDirection: isDesktopScreen ? 'row' : 'column',
          flex: 1,
        }}
      >
        <ColumnContainer
          dataTestId='stripe-checkout-page-summary'
          bgcolor={palette.background.paperElevation0}
          isLargeScreen={isDesktopScreen}
        >
          <StripeCheckoutSummary
            planLabel={t(PLAN_LABELS[plan])}
            interval={interval}
            onIntervalChange={handleIntervalChange}
            promoInput={promoInput}
            onPromoInputChange={setPromoInput}
            onApplyPromo={handleApplyPromo}
            appliedPromoLabel={appliedPromoLabel}
            onRemoveAppliedPromo={handleRemoveAppliedPromo}
            promoError={promoError}
            promoReduction={promoReduction}
            applying={mutating}
            trial={trial}
            trialDays={activeCheckout?.recurring?.trial?.trialPeriodDays}
            refRabattOn={refRabattOn}
            onRefRabattChange={handleRefRabattChange}
            showRefRabattToggle={showRefRabattToggle}
            pricePanel={<OrderTotalSummary checkout={activeCheckout} />}
            priceTable={<CheckoutPriceTable checkout={activeCheckout} />}
          />
        </ColumnContainer>

        <ColumnContainer
          dataTestId='stripe-checkout-page-form'
          bgcolor={palette.background.paperElevation2}
          isLargeScreen={isDesktopScreen}
        >
          <EmailInputSection email={email} onEmailChange={setEmail} state={emailCheckState} />
          {errorCode && (
            <Alert
              severity='error'
              data-testid={`stripe-checkout-error-${errorCode}`}
              sx={{ marginBottom: spaces.md }}
            >
              {t(ERROR_LABELS[errorCode])}
            </Alert>
          )}
          {paymentStack}
          {hasAnySession && (
            <Box
              component='form'
              onSubmit={handleSubmit}
              data-testid='stripe-checkout-form'
              sx={{ display: 'flex', flexDirection: 'column', gap: spaces.lg, marginTop: spaces.lg }}
            >
              <CheckoutTerms
                termsAccepted={termsAccepted}
                onTermsChange={handleTermsChange}
                termsError={termsError}
                newsletter={newsletter}
                onNewsletterChange={setNewsletter}
              />
              <Box data-testid='stripe-checkout-captcha' sx={{ display: 'flex', justifyContent: 'center' }}>
                {renderCaptcha()}
              </Box>
              <Button
                type='submit'
                variant='contained'
                color='primary'
                size='large'
                disabled={submitDisabled}
                fullWidth
                data-testid='stripe-checkout-submit'
                sx={{ position: 'relative' }}
              >
                <Box component='span' sx={{ visibility: submitting ? 'hidden' : 'visible' }}>
                  {trial ? t('Start premium trial') : t('Pay now')}
                </Box>
                {submitting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                    }}
                  >
                    <CircularProgress
                      size={20}
                      color='inherit'
                      sx={{ '& svg': { width: '20px', height: '20px' } }}
                    />
                  </Box>
                )}
              </Button>
              {trial && <TrialCostNotice checkout={activeCheckout} />}
            </Box>
          )}
        </ColumnContainer>
      </Box>
    </Box>
  );
};

export default StripeCheckoutPage;
